/**
 * Store de Checklists - Zustand
 *
 * Maneja el estado de los checklists/packs con:
 * - Persistencia automatica en localStorage
 * - Acciones inmutables con Immer
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import type {
  Checklist,
  ChecklistItem,
  CreateChecklistInput,
  CreateItemInput,
  ChecklistProgress,
} from '@/core/types';
import { generateId, getNowISO } from '@/core/utils';
import { DEFAULT_CHECKLISTS } from '@/data/initialChecklists';

interface ChecklistState {
  // === State ===
  checklists: Checklist[];
  activeChecklistId: string | null;
  isInitialized: boolean;

  // === Actions: Checklists ===
  addChecklist: (input: CreateChecklistInput) => string;
  updateChecklist: (id: string, updates: Partial<Checklist>) => void;
  deleteChecklist: (id: string) => void;
  reorderChecklists: (ids: string[]) => void;
  setActiveChecklist: (id: string) => void;

  // === Actions: Items ===
  addItem: (checklistId: string, item: CreateItemInput) => string;
  updateItem: (
    checklistId: string,
    itemId: string,
    updates: Partial<ChecklistItem>
  ) => void;
  deleteItem: (checklistId: string, itemId: string) => void;
  toggleItem: (checklistId: string, itemId: string) => void;
  reorderItems: (checklistId: string, itemIds: string[]) => void;

  // === Actions: Bulk ===
  resetChecklist: (checklistId: string) => void;
  resetAllChecklists: () => void;

  // === Actions: Data ===
  initialize: () => void;
}

export const useChecklistStore = create<ChecklistState>()(
  persist(
    immer((set, get) => ({
      // === Initial State ===
      checklists: [],
      activeChecklistId: null,
      isInitialized: false,

      // === Checklist CRUD ===
      addChecklist: (input) => {
        const id = generateId('chk');
        const now = getNowISO();
        const maxOrder = Math.max(0, ...get().checklists.map((c) => c.order));

        set((state) => {
          state.checklists.push({
            ...input,
            id,
            items: input.items.map((item, idx) => ({
              ...item,
              id: generateId('item'),
              isChecked: false,
              order: idx,
            })),
            isDefault: false,
            order: maxOrder + 1,
            createdAt: now,
            updatedAt: now,
          });
        });

        return id;
      },

      updateChecklist: (id, updates) => {
        set((state) => {
          const checklist = state.checklists.find((c) => c.id === id);
          if (checklist) {
            Object.assign(checklist, updates, { updatedAt: getNowISO() });
          }
        });
      },

      deleteChecklist: (id) => {
        const checklist = get().checklists.find((c) => c.id === id);
        if (checklist?.isDefault) return; // No eliminar defaults

        set((state) => {
          state.checklists = state.checklists.filter((c) => c.id !== id);
          if (state.activeChecklistId === id) {
            state.activeChecklistId = state.checklists[0]?.id ?? null;
          }
        });
      },

      reorderChecklists: (ids) => {
        set((state) => {
          ids.forEach((id, index) => {
            const checklist = state.checklists.find((c) => c.id === id);
            if (checklist) checklist.order = index;
          });
        });
      },

      setActiveChecklist: (id) => {
        set({ activeChecklistId: id });
      },

      // === Item CRUD ===
      addItem: (checklistId, item) => {
        const id = generateId('item');

        set((state) => {
          const checklist = state.checklists.find((c) => c.id === checklistId);
          if (checklist) {
            const maxOrder = Math.max(0, ...checklist.items.map((i) => i.order));
            checklist.items.push({
              ...item,
              id,
              isChecked: false,
              order: maxOrder + 1,
            });
            checklist.updatedAt = getNowISO();
          }
        });

        return id;
      },

      updateItem: (checklistId, itemId, updates) => {
        set((state) => {
          const checklist = state.checklists.find((c) => c.id === checklistId);
          if (checklist) {
            const item = checklist.items.find((i) => i.id === itemId);
            if (item) {
              Object.assign(item, updates);
              checklist.updatedAt = getNowISO();
            }
          }
        });
      },

      deleteItem: (checklistId, itemId) => {
        set((state) => {
          const checklist = state.checklists.find((c) => c.id === checklistId);
          if (checklist) {
            checklist.items = checklist.items.filter((i) => i.id !== itemId);
            checklist.updatedAt = getNowISO();
          }
        });
      },

      toggleItem: (checklistId, itemId) => {
        set((state) => {
          const checklist = state.checklists.find((c) => c.id === checklistId);
          if (checklist) {
            const item = checklist.items.find((i) => i.id === itemId);
            if (item) {
              item.isChecked = !item.isChecked;
            }
          }
        });
      },

      reorderItems: (checklistId, itemIds) => {
        set((state) => {
          const checklist = state.checklists.find((c) => c.id === checklistId);
          if (checklist) {
            itemIds.forEach((id, index) => {
              const item = checklist.items.find((i) => i.id === id);
              if (item) item.order = index;
            });
            checklist.updatedAt = getNowISO();
          }
        });
      },

      // === Bulk Actions ===
      resetChecklist: (checklistId) => {
        set((state) => {
          const checklist = state.checklists.find((c) => c.id === checklistId);
          if (checklist) {
            checklist.items.forEach((item) => {
              item.isChecked = false;
            });
          }
        });
      },

      resetAllChecklists: () => {
        set((state) => {
          state.checklists.forEach((checklist) => {
            checklist.items.forEach((item) => {
              item.isChecked = false;
            });
          });
        });
      },

      // === Initialization ===
      initialize: () => {
        const state = get();

        if (state.checklists.length === 0) {
          set({
            checklists: DEFAULT_CHECKLISTS,
            activeChecklistId: DEFAULT_CHECKLISTS[0]?.id ?? null,
            isInitialized: true,
          });
          return;
        }

        // SIEMPRE sincronizar con DEFAULT_CHECKLISTS para mantener datos actualizados
        set((draft) => {
          // Actualizar checklists existentes con items actualizados
          draft.checklists.forEach((existingChecklist) => {
            const defaultChecklist = DEFAULT_CHECKLISTS.find(
              (c) => c.id === existingChecklist.id
            );

            if (defaultChecklist && existingChecklist.isDefault) {
              // SIEMPRE sincronizar items: preservar isChecked del usuario, pero actualizar metadatos
              existingChecklist.items = defaultChecklist.items.map((defaultItem) => {
                const existingItem = existingChecklist.items.find(
                  (i) => i.id === defaultItem.id
                );

                return {
                  ...defaultItem, // Tomar todos los campos actualizados (label, priority, category, etc.)
                  isChecked: existingItem?.isChecked ?? defaultItem.isChecked, // Preservar estado del usuario
                };
              });
            }
          });

          // Agregar nuevos checklists default que no existen
          const existingIds = new Set(draft.checklists.map((c) => c.id));
          const newDefaults = DEFAULT_CHECKLISTS.filter(
            (c) => !existingIds.has(c.id)
          );
          draft.checklists.push(...newDefaults);

          if (!draft.activeChecklistId && draft.checklists.length > 0) {
            draft.activeChecklistId = draft.checklists[0]?.id ?? null;
          }
          draft.isInitialized = true;
        });
      },
    })),
    {
      name: 'exocortex-checklists',
      version: 3, // v3: Forzar migración completa de items
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        checklists: state.checklists,
        activeChecklistId: state.activeChecklistId,
      }),
      migrate: (persistedState, version) => {
        console.log('[Checklist Migration] Detected version:', version, '-> migrating to v3');
        const state = persistedState as { checklists: Checklist[] };

        if (state.checklists) {
          // Sincronizar TODOS los checklists default con datos frescos
          state.checklists.forEach((existingChecklist) => {
            const defaultChecklist = DEFAULT_CHECKLISTS.find(
              (c) => c.id === existingChecklist.id
            );
            if (defaultChecklist && existingChecklist.isDefault) {
              existingChecklist.items = defaultChecklist.items.map((defaultItem) => {
                const existingItem = existingChecklist.items.find(
                  (i) => i.id === defaultItem.id
                );
                return {
                  ...defaultItem,
                  isChecked: existingItem?.isChecked ?? defaultItem.isChecked,
                };
              });
            }
          });

          // Agregar nuevos checklists default que no existen
          const existingIds = new Set(state.checklists.map((c) => c.id));
          const newDefaults = DEFAULT_CHECKLISTS.filter(
            (c) => !existingIds.has(c.id)
          );
          state.checklists.push(...newDefaults);

          console.log('[Checklist Migration] Completed. Total checklists:', state.checklists.length);
        }

        return persistedState as ChecklistState;
      },
    }
  )
);

// === Selectores ===

/**
 * Selector para el checklist activo
 */
export const selectActiveChecklist = (state: ChecklistState) =>
  state.checklists.find((c) => c.id === state.activeChecklistId);

/**
 * Selector para checklists ordenados
 */
export const selectOrderedChecklists = (state: ChecklistState) =>
  [...state.checklists].sort((a, b) => a.order - b.order);

/**
 * Selector para progreso de un checklist
 */
export const selectChecklistProgress =
  (checklistId: string) =>
  (state: ChecklistState): ChecklistProgress => {
    const checklist = state.checklists.find((c) => c.id === checklistId);
    if (!checklist) {
      return {
        total: 0,
        checked: 0,
        percentage: 0,
        criticalTotal: 0,
        criticalChecked: 0,
        allCriticalDone: true,
      };
    }

    const total = checklist.items.length;
    const checked = checklist.items.filter((i) => i.isChecked).length;
    const criticalItems = checklist.items.filter((i) => i.priority === 'critical');
    const criticalChecked = criticalItems.filter((i) => i.isChecked).length;

    return {
      total,
      checked,
      percentage: total > 0 ? Math.round((checked / total) * 100) : 0,
      criticalTotal: criticalItems.length,
      criticalChecked,
      allCriticalDone:
        criticalItems.length === 0 || criticalChecked === criticalItems.length,
    };
  };

/**
 * Selector para items agrupados por categoria
 */
export const selectItemsByCategory =
  (checklistId: string) => (state: ChecklistState) => {
    const checklist = state.checklists.find((c) => c.id === checklistId);
    if (!checklist) return {};

    const result: Record<string, ChecklistItem[]> = {};
    checklist.items
      .sort((a, b) => a.order - b.order)
      .forEach((item) => {
        if (!result[item.category]) result[item.category] = [];
        result[item.category]?.push(item);
      });
    return result;
  };
